import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, TruncatedText } from "@/components/ui";
import { cn } from "@/lib/utils";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import type { ServiceCategory } from "@/types";

interface SortableServiceCategoryItemProps {
  category: ServiceCategory;
  orderIndex: number;
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

export function SortableServiceCategoryItem({
  category,
  orderIndex,
  onEdit,
  onDelete,
  disabled = false,
}: SortableServiceCategoryItemProps) {
  const { t, i18n } = useTranslation("activities");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const name = resolveCatalogTranslation(
    category.name_translations,
    i18n.language,
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group border-surface-200 bg-surface-0 flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-[box-shadow,border-color]",
        isDragging
          ? "shadow-popover ring-primary-500/40 z-50 ring-2"
          : "hover:border-surface-300",
      )}
    >
      <button
        type="button"
        className={cn(
          "text-surface-300 hover:text-surface-500 touch-none rounded p-0.5",
          disabled && "cursor-not-allowed opacity-40",
        )}
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 shrink-0 cursor-grab" />
      </button>

      <span
        className="bg-surface-100 text-surface-600 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold tabular-nums"
        aria-hidden
      >
        {orderIndex}
      </span>

      <TruncatedText
        as="p"
        className="text-surface-900 flex-1 text-sm font-medium"
      >
        {name}
      </TruncatedText>

      <div className="flex shrink-0 items-center gap-1 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onEdit}
          disabled={disabled}
          aria-label={t("serviceCategories.edit")}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onDelete}
          disabled={disabled}
          aria-label={t("serviceCategories.delete")}
        >
          <Trash2 className="text-error-600 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
